import * as Dfns from 'date-fns/fp';
import {
  B2BCreativeTags,
  CreativeInfoItem,
  CreativeInsightsIsciData,
  Importance
} from '@blisspointmedia/bpm-types/dist/Creative';
import { CreativeMap, CreativeMapItem } from '@src/redux/creative';
import { getChannelSeriesColor } from '@src/utils/colors';
import {
  B2B_CONCEPT,
  CONCEPT_ATTRIBUTE,
  ConceptMap,
  ConceptOptions,
  EMPTY_SELECTION,
  EnabledKpis,
  ImportanceData,
  ImportanceResponse,
  LANGUAGE_ATTRIBUTE,
  MISSING_END_DATE,
  NOT_PRESENT_TAG,
  Selection,
  SelectionAction,
  SelectionActionType
} from './creative-insights-constants';

const DATE_RANGE_FORMAT = 'MM-dd-yyyy';

export const getFillColors = (
  attributeImportanceData: Importance[],
  suppressedAttributes: Set<string>
): Map<string, string> => {
  const colors = new Map<string, string>();
  attributeImportanceData.forEach((attribute, i) => {
    if (!suppressedAttributes.has(attribute.name)) {
      colors.set(attribute.name, getChannelSeriesColor(i));
    }
  });
  return colors;
};

const getEndDateValue = (endDate: string | undefined): number =>
  new Date(endDate || MISSING_END_DATE).valueOf();

export const compareByEndDateDesc = (endDateA?: string, endDateB?: string): number =>
  getEndDateValue(endDateB) - getEndDateValue(endDateA);

// Adds each creative's most recent date range and resolves its tags, newest creatives first.
export const prepareCreativeList = (
  creativeMap: CreativeMap,
  b2bCreativeTags: B2BCreativeTags
): CreativeMapItem[] =>
  Object.values(creativeMap)
    .map(creative => {
      const [mostRecentRange] = [...creative.ranges].sort((rangeA, rangeB) =>
        compareByEndDateDesc(rangeA.endDate, rangeB.endDate)
      );
      // The creative map omits creativeTags for creatives with no tags.
      const creativeTags =
        creative.concept === B2B_CONCEPT
          ? (b2bCreativeTags[creative.isci] ?? {})
          : (creative.creativeTags ?? {});
      return {
        ...creative,
        creativeTags,
        mostRecentDateRange: mostRecentRange
      };
    })
    .sort((creativeA, creativeB) =>
      compareByEndDateDesc(
        creativeA.mostRecentDateRange?.endDate,
        creativeB.mostRecentDateRange?.endDate
      )
    );

export const creativeMatchesTag = (
  creative: CreativeInfoItem,
  attribute: string,
  tag: string,
  attributeTypeBinomial: Record<string, boolean>
): boolean => {
  if (attribute === CONCEPT_ATTRIBUTE) {
    return creative.concept === tag;
  }
  if (attribute === LANGUAGE_ATTRIBUTE) {
    return creative.language === tag;
  }
  const attributeTags = creative.creativeTags[attribute];
  if (attributeTypeBinomial[attribute]) {
    if (tag === attribute) {
      return Boolean(attributeTags);
    }
    return tag === `No ${attribute}` && !attributeTags;
  }
  if (tag === NOT_PRESENT_TAG) {
    return !attributeTags;
  }
  return Boolean(attributeTags?.includes(tag));
};

export const formatDateRange = ({ startDate, endDate }: { startDate: string; endDate: string }) => {
  const start = Dfns.format(DATE_RANGE_FORMAT, Dfns.parseISO(startDate));
  return endDate ? `${start} to ${Dfns.format(DATE_RANGE_FORMAT, Dfns.parseISO(endDate))}` : start;
};

// Groups creatives by model entry into one item per modeled creative, skipping creatives
// the attribute doesn't apply to and creatives that never aired.
export const buildCreativeInfoList = (
  attributeName: string,
  sortedCreativeList: CreativeMapItem[],
  conceptMap: ConceptMap,
  isciData: CreativeInsightsIsciData
): CreativeInfoItem[] => {
  const appliesToAllConcepts =
    attributeName === CONCEPT_ATTRIBUTE || attributeName === LANGUAGE_ATTRIBUTE;

  const creativesByModelEntry = new Map<number, CreativeMapItem[]>();
  sortedCreativeList.forEach(creative => {
    const { modelEntry } = creative;
    if (!modelEntry) {
      return;
    }
    if (!appliesToAllConcepts && conceptMap[creative.concept]?.has(attributeName)) {
      return;
    }
    const group = creativesByModelEntry.get(modelEntry);
    if (group) {
      group.push(creative);
    } else {
      creativesByModelEntry.set(modelEntry, [creative]);
    }
  });

  return [...creativesByModelEntry.values()].flatMap(creatives => {
    const aired = creatives.filter(
      creative => creative.mostRecentDateRange && isciData[creative.isci]?.numSpots
    );
    if (!aired.length) {
      return [];
    }
    // The group takes its details from its last aired creative.
    const { name, isci, file, concept, creativeTags, length, language } = aired[aired.length - 1];
    return [
      {
        name,
        isci,
        file,
        isciList: aired.map(creative => creative.isci),
        concept,
        creativeTags,
        length,
        language,
        spend: aired.reduce((total, creative) => total + (isciData[creative.isci].spend ?? 0), 0),
        numSpots: aired.reduce((total, creative) => total + isciData[creative.isci].numSpots, 0),
        mostRecentDateRanges: [
          ...new Set(
            aired.flatMap(({ mostRecentDateRange }) =>
              mostRecentDateRange ? [formatDateRange(mostRecentDateRange)] : []
            )
          )
        ]
      }
    ];
  });
};

export const getCreativeKey = (creative: CreativeInfoItem): string => creative.isciList.join(',');

// Internal users also see the KPIs that are not enabled for clients, flagged as internal.
export const toEnabledKpis = (
  rows: { cid: string; enabled: boolean }[],
  isInternal: boolean
): EnabledKpis => ({
  viewableKpis: rows.filter(kpi => kpi.enabled || isInternal).map(kpi => kpi.cid),
  internalKpis: new Set(rows.filter(kpi => !kpi.enabled && isInternal).map(kpi => kpi.cid))
});

export const toImportanceData = ({
  creativeInsights,
  attributeTypeBinomial,
  lastModified
}: ImportanceResponse): ImportanceData => ({
  attributeImportanceData: creativeInsights.attributeImportanceData,
  tagImportanceData: creativeInsights.tagImportanceData,
  attributeTypeBinomial,
  lastModified: lastModified
    ? Dfns.format('M/d/yyyy', Dfns.parseISO(lastModified))
    : 'Info not available'
});

export const toSuppressedAttributes = (attributes: string | null): Set<string> =>
  new Set(attributes ? attributes.split(',') : []);

// Maps each concept to the attributes disabled for it.
export const toConceptMap = ({ conceptData }: ConceptOptions): ConceptMap =>
  Object.fromEntries(
    conceptData.flatMap(({ concept, categories }) => {
      const disabled = Object.keys(categories).filter(attribute => !categories[attribute]);
      return disabled.length ? [[concept, new Set(disabled)]] : [];
    })
  );

export const selectionReducer = (state: Selection, action: SelectionAction): Selection => {
  switch (action.type) {
    case SelectionActionType.TOGGLE_ATTRIBUTE:
      return {
        ...EMPTY_SELECTION,
        attribute: action.attribute === state.attribute ? '' : action.attribute
      };
    case SelectionActionType.SELECT_ATTRIBUTE:
      return { ...EMPTY_SELECTION, attribute: action.attribute };
    case SelectionActionType.CLEAR_ATTRIBUTE:
      return EMPTY_SELECTION;
    case SelectionActionType.TOGGLE_TAG:
      return { ...state, tag: action.tag === state.tag ? '' : action.tag, creativeKey: '' };
    case SelectionActionType.SELECT_TAG:
      return { ...state, tag: action.tag, creativeKey: '' };
    case SelectionActionType.CLEAR_TAG:
      return { ...state, tag: '', creativeKey: '' };
    case SelectionActionType.SELECT_CREATIVE:
      return { ...state, creativeKey: action.creativeKey };
    default:
      return state;
  }
};
